import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from '@chakra-ui/react';
import { useState } from 'react';

import { Switch } from '@revelio/ui';

import {
  useDataBuilderActions,
  useDataBuilderSelector,
} from '../data-builder.store';
import { dataLagDisplayValue } from '../utils/data-lag-helper';

const CUSTOM_OPTION_VALUE = 'custom';
const DEFAULT_DATA_LAG = 12;
const LAG_OPTIONS = [
  { value: '0', label: 'No lag' },
  { value: '6', label: '6 months' },
  { value: '9', label: '9 months' },
  { value: '12', label: '1 year' },
  { value: CUSTOM_OPTION_VALUE, label: 'Choose Lag' },
];

export const DataBuilderDataLagControl = () => {
  const lagData = useDataBuilderSelector((state) => state.lagData);
  const { setLagData } = useDataBuilderActions();
  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customMonthsInput, setCustomMonthsInput] = useState('');

  const openCustomModal = () => {
    const isPreset = LAG_OPTIONS.some((opt) => opt.value === String(lagData));
    setCustomMonthsInput(!isPreset && lagData > 0 ? String(lagData) : '');
    setIsCustomModalOpen(true);
  };

  const closeCustomModal = () => {
    setIsCustomModalOpen(false);
  };

  const handleLagOptionSelect = (value: string | undefined) => {
    if (value === undefined) {
      return;
    }

    if (value === CUSTOM_OPTION_VALUE) {
      openCustomModal();
      return;
    }

    const nextValue = Number(value);
    if (Number.isFinite(nextValue)) {
      setLagData(nextValue);
    }
  };

  const handleCustomApply = () => {
    if (customMonthsInput.trim() === '') {
      return;
    }

    const nextMonths = Number(customMonthsInput);
    if (!Number.isFinite(nextMonths) || nextMonths < 0) {
      return;
    }

    const normalizedMonths = Math.floor(nextMonths);
    setLagData(normalizedMonths);
    closeCustomModal();
  };

  return (
    <>
      <Flex alignItems="center" gap={2}>
        <Switch
          size="sm"
          aria-label="Data Lag"
          checked={lagData > 0}
          onChange={(event) => {
            const nextValue = event.target.checked ? DEFAULT_DATA_LAG : 0;
            setLagData(nextValue);
          }}
        />
        <Menu placement="bottom-start">
          {() => (
            <>
              <MenuButton as={Button} variant="link" fontSize="sm">
                {dataLagDisplayValue(lagData)}
              </MenuButton>
              <MenuList>
                <MenuOptionGroup
                  type="radio"
                  value={String(lagData)}
                  onChange={(nextValue) => {
                    handleLagOptionSelect(
                      Array.isArray(nextValue) ? nextValue[0] : nextValue
                    );
                  }}
                >
                  {LAG_OPTIONS.map((option) => (
                    <MenuItemOption key={option.value} value={option.value}>
                      {option.label}
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </>
          )}
        </Menu>
      </Flex>

      <Modal isOpen={isCustomModalOpen} onClose={closeCustomModal} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="text.primary">Select Data Lag</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="text.primary">
                Lag (months)
              </FormLabel>
              <NumberInput
                min={0}
                step={1}
                value={customMonthsInput}
                onChange={(valueAsString) =>
                  setCustomMonthsInput(valueAsString)
                }
              >
                <NumberInputField
                  inputMode="numeric"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleCustomApply();
                    }
                  }}
                />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              onClick={closeCustomModal}
              colorScheme="navyBlue"
            >
              Cancel
            </Button>
            <Button onClick={handleCustomApply} colorScheme="green">
              Apply
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
